import { useEffect, useState, useRef } from 'react';
import { api, privateFileUrl } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import { ResponsiveContainer, AreaChart, Area, LineChart, Line, XAxis, YAxis, Tooltip } from 'recharts';
import { ScoreRing } from '@/components/ScoreRing';
import { Scale, Plus, Ruler, Camera, Trophy, TrendingDown, Flame, Upload, Award, Activity, Calendar } from 'lucide-react';
import { useProgramLocked, LockedScreen } from '@/components/ProgramLock';

const imgSrc = (p) => privateFileUrl(p);

export default function Progress() {
  const locked = useProgramLocked();
  const { user, refresh } = useAuth();
  const [weights, setWeights] = useState([]);
  const [measurements, setMeasurements] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [history, setHistory] = useState(null);
  const [summary, setSummary] = useState(null);

  const load = async () => {
    const [w, m, p, h, s] = await Promise.all([
      api.get('/weight'), api.get('/measurements'), api.get('/progress-photos'),
      api.get('/regen-score/history'), api.get('/progress/summary'),
    ]);
    setWeights(w.data); setMeasurements(m.data); setPhotos(p.data); setHistory(h.data); setSummary(s.data);
  };
  useEffect(() => { if (!locked) load(); }, []);

  const sumCards = [
    { label: 'Weight Lost', value: summary ? `-${summary.weight_lost}` : '—', unit: 'kg', icon: Scale, color: '#65A30D' },
    { label: 'Waist Lost', value: summary ? `-${summary.waist_lost}` : '—', unit: 'cm', icon: Ruler, color: '#00C8FF' },
    { label: 'Recovery', value: summary ? `${summary.recovery_improvement > 0 ? '+' : ''}${summary.recovery_improvement}` : '—', unit: 'pts', icon: Activity, color: '#A78BFA' },
    { label: 'Consistency', value: summary ? `${summary.consistency}` : '—', unit: '%', icon: Flame, color: '#FFB800' },
    { label: 'Days', value: summary ? `${summary.days_on_program}` : '—', unit: 'on program', icon: Calendar, color: '#7DD3FC' },
  ];

  if (locked) return <LockedScreen title="Progress Tracking" color="#FFB800" />;
  return (
    <div className="space-y-5 pb-4" data-testid="client-progress">
      <div>
        <div className="label-mini text-[#00C8FF]">Tracking</div>
        <h1 className="font-display font-black text-3xl mt-1">Progress</h1>
      </div>

      {/* Transformation Summary */}
      <div className="regen-card-elevated p-5 blueprint-grid" data-testid="transformation-summary">
        <div className="flex items-center gap-2 mb-4"><Trophy className="w-5 h-5 text-[var(--c-green-ink)]" /><span className="font-display font-bold">Total Progress</span></div>
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
          {sumCards.map(c => (
            <div key={c.label} className="text-center">
              <c.icon className="w-4 h-4 mx-auto" style={{ color: c.color }} />
              <div className="font-mono text-xl mt-1.5" style={{ color: c.color }}>{c.value}</div>
              <div className="text-[9px] uppercase tracking-wider text-muted-foreground mt-0.5">{c.unit}</div>
              <div className="text-[10px] text-muted-foreground">{c.label}</div>
            </div>
          ))}
        </div>
      </div>

      <Tabs defaultValue="weight">
        <TabsList className="bg-regen-card border border-[var(--c-hair)] w-full grid grid-cols-4">
          <TabsTrigger value="weight" data-testid="pr-tab-weight">Weight</TabsTrigger>
          <TabsTrigger value="measure" data-testid="pr-tab-measure">Measure</TabsTrigger>
          <TabsTrigger value="photos" data-testid="pr-tab-photos">Photos</TabsTrigger>
          <TabsTrigger value="score" data-testid="pr-tab-score">Score</TabsTrigger>
        </TabsList>

        <TabsContent value="weight" className="mt-4 space-y-4"><WeightTab weights={weights} user={user} reload={() => { load(); refresh(); }} /></TabsContent>
        <TabsContent value="measure" className="mt-4 space-y-4"><MeasureTab measurements={measurements} reload={load} /></TabsContent>
        <TabsContent value="photos" className="mt-4"><PhotosTab photos={photos} reload={load} /></TabsContent>
        <TabsContent value="score" className="mt-4"><ScoreTab history={history} /></TabsContent>
      </Tabs>
    </div>
  );
}

function weeklyChange(weights) {
  const recent = weights.filter(w => w.date >= new Date(Date.now() - 30 * 864e5).toISOString().slice(0, 10));
  if (recent.length < 2) return 0;
  const d0 = new Date(recent[0].date), d1 = new Date(recent[recent.length - 1].date);
  const days = Math.max(1, Math.round((d1 - d0) / 864e5));
  return Math.round((recent[recent.length - 1].weight_kg - recent[0].weight_kg) / days * 7 * 100) / 100;
}

function WeightTab({ weights, user, reload }) {
  const [w, setW] = useState('');
  const add = async () => {
    if (!w) return;
    try { await api.post('/weight', { weight_kg: parseFloat(w) }); setW(''); toast.success('Logged'); reload(); }
    catch { toast.error('Failed'); }
  };
  const start = user?.starting_weight_kg, cur = weights[weights.length - 1]?.weight_kg ?? user?.current_weight_kg, goal = user?.goal_weight_kg;
  const lost = start && cur ? (start - cur).toFixed(1) : '0.0';
  const wc = weeklyChange(weights);
  return (
    <>
      <div className="regen-card-elevated p-4 space-y-2">
        <div className="flex items-center gap-3">
          <Scale className="w-5 h-5 text-[#00C8FF]" />
          <Input type="number" step="0.1" placeholder="This week's weight (kg)" value={w} onChange={e => setW(e.target.value)} data-testid="weight-input" className="bg-transparent border-[var(--c-hair)] font-mono" />
          <Button onClick={add} data-testid="log-weight-btn" className="bg-[#00C8FF] text-[#081726] font-semibold"><Plus className="w-4 h-4" /></Button>
        </div>
        <p className="text-[11px] text-muted-foreground leading-snug" data-testid="weight-guidance">
          Update your weight <span className="text-[#00C8FF] font-semibold">once per week</span>, in the morning, after using the toilet, before eating or drinking.
        </p>
      </div>
      <div className="grid grid-cols-5 gap-2">
        <KCard label="Start" value={start ?? '—'} unit="kg" />
        <KCard label="Current" value={cur ?? '—'} unit="kg" color="#00C8FF" />
        <KCard label="Goal" value={goal ?? '—'} unit="kg" />
        <KCard label="Lost" value={`-${lost}`} unit="kg" color='#65A30D' />
        <KCard label="Weekly" value={`${wc > 0 ? '+' : ''}${wc}`} unit="kg" color={wc <= 0 ? '#65A30D' : '#FFB800'} />
      </div>
      <div className="regen-card-elevated p-5">
        <div className="label-mini">Weight Trend</div>
        <div className="h-48 mt-3">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={weights.map(x => ({ date: x.date, weight: x.weight_kg }))} margin={{ left: -18, right: 6, top: 6 }}>
              <defs><linearGradient id="wG" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#00C8FF" stopOpacity={0.5} /><stop offset="100%" stopColor="#00C8FF" stopOpacity={0} /></linearGradient></defs>
              <XAxis dataKey="date" tickFormatter={d => d.slice(5)} tick={{ fontSize: 9, fill: '#7E8C9E' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
              <YAxis domain={['dataMin - 2', 'dataMax + 2']} tick={{ fontSize: 9, fill: '#7E8C9E' }} axisLine={false} tickLine={false} width={30} />
              <Tooltip contentStyle={{ background: '#101E30', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12 }} />
              <Area type="monotone" dataKey="weight" stroke="#00C8FF" strokeWidth={2} fill="url(#wG)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </>
  );
}

const MEASURE_FIELDS = [
  { k: 'waist_cm', label: 'Waist', color: '#00C8FF' },
  { k: 'hips_cm', label: 'Hips', color: '#FFB800' },
  { k: 'chest_cm', label: 'Chest', color: '#65A30D' },
  { k: 'l_arm_cm', label: 'L Arm', color: '#A78BFA' },
  { k: 'r_arm_cm', label: 'R Arm', color: '#A78BFA' },
  { k: 'l_thigh_cm', label: 'L Thigh', color: '#7DD3FC' },
  { k: 'r_thigh_cm', label: 'R Thigh', color: '#7DD3FC' },
];

function MeasureTab({ measurements, reload }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({});
  const save = async () => {
    const payload = {};
    MEASURE_FIELDS.forEach(m => { if (f[m.k]) payload[m.k] = parseFloat(f[m.k]); });
    if (Object.keys(payload).length === 0) return toast.error('Add at least one value');
    try { await api.post('/measurements', payload); toast.success('Saved'); setOpen(false); setF({}); reload(); }
    catch { toast.error('Failed'); }
  };
  const latest = measurements[measurements.length - 1];
  const firstFor = (k) => measurements.find(m => m[k] != null);
  const waistStart = firstFor('waist_cm')?.waist_cm, waistCur = [...measurements].reverse().find(m => m.waist_cm != null)?.waist_cm;
  const waistLost = (waistStart && waistCur) ? +(waistStart - waistCur).toFixed(1) : 0;
  const waistPct = waistLost > 0 ? Math.min(100, Math.round(waistLost / 10 * 100)) : 0;
  const waistSeries = measurements.filter(m => m.waist_cm != null).map(m => ({ date: m.date, waist: m.waist_cm }));
  const [sel, setSel] = useState('waist_cm');
  const selField = MEASURE_FIELDS.find(m => m.k === sel) || MEASURE_FIELDS[0];
  const selSeries = measurements.filter(m => m[sel] != null).map(m => ({ date: m.date, val: m[sel] }));

  return (
    <>
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-measure-btn" className="bg-[var(--c-green)] text-[#081726] font-semibold"><Plus className="w-4 h-4 mr-1.5" /> Log Measurements</Button></DialogTrigger>
          <DialogContent className="bg-regen-card border-[var(--c-hair)] max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Body Measurements (cm)</DialogTitle></DialogHeader>
            <div className="space-y-3">
              {MEASURE_FIELDS.map(m => (
                <div key={m.k}><Label>{m.label}</Label><Input type="number" step="0.1" value={f[m.k] || ''} onChange={e => setF({ ...f, [m.k]: e.target.value })} data-testid={`m-${m.k}`} className="bg-transparent border-[var(--c-hair)] font-mono" /></div>
              ))}
              <Button onClick={save} data-testid="m-save" className="w-full bg-[var(--c-green)] text-[#081726] font-semibold">Save</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Waist hero */}
      <div className="regen-card-elevated p-5 flex items-center gap-5" data-testid="waist-hero">
        <ScoreRing value={waistPct} color="#00C8FF" size={120} stroke={11}
          center={<div className="flex flex-col items-center"><Ruler className="w-4 h-4 text-[#00C8FF]" /><div className="font-mono text-2xl font-bold mt-0.5">{waistPct}%</div></div>} />
        <div className="flex-1">
          <div className="label-mini text-[#00C8FF]">Waist — Primary Metric</div>
          <div className="font-mono text-xl mt-1">{waistStart ?? '—'} cm → {waistCur ?? '—'} cm</div>
          <div className="text-sm font-semibold text-[var(--c-green-ink)] mt-1">{waistLost > 0 ? `-${waistLost} cm lost` : 'Log your waist to track'}</div>
        </div>
      </div>

      {/* Selected-metric trend (tap any tile below) */}
      <div className="regen-card-elevated p-5" data-testid="measure-trend">
        <div className="flex items-center justify-between">
          <div className="label-mini" style={{ color: selField.color }}>{selField.label} Trend</div>
          <span className="text-[11px] text-muted-foreground">{selSeries.length ? `${selSeries.length} entries` : 'No data'}</span>
        </div>
        {selSeries.length > 1 ? (
          <div className="h-36 mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={selSeries} margin={{ left: -18, right: 6, top: 6 }}>
                <defs><linearGradient id="selG" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={selField.color} stopOpacity={0.4} /><stop offset="100%" stopColor={selField.color} stopOpacity={0} /></linearGradient></defs>
                <XAxis dataKey="date" tickFormatter={d => d.slice(5)} tick={{ fontSize: 9, fill: '#7E8C9E' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                <YAxis domain={['dataMin - 2', 'dataMax + 2']} tick={{ fontSize: 9, fill: '#7E8C9E' }} axisLine={false} tickLine={false} width={30} />
                <Tooltip contentStyle={{ background: '#101E30', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12 }} />
                <Area type="monotone" dataKey="val" name={selField.label} stroke={selField.color} strokeWidth={2} fill="url(#selG)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-36 mt-3 flex items-center justify-center text-sm text-muted-foreground text-center px-4">
            {selSeries.length === 1 ? `Log ${selField.label} again to see the trend graph.` : `No ${selField.label} entries yet — tap "Log Measurements" to start tracking.`}
          </div>
        )}
      </div>

      <div className="grid grid-cols-3 gap-2">
        {MEASURE_FIELDS.map(m => {
          const fst = firstFor(m.k)?.[m.k];
          const change = (latest?.[m.k] && fst) ? (latest[m.k] - fst).toFixed(1) : null;
          const active = sel === m.k;
          return (
            <button key={m.k} onClick={() => setSel(m.k)} className={`regen-card-elevated p-3 text-center transition active:scale-95 ${active ? 'ring-2' : ''}`}
              style={active ? { boxShadow: `inset 0 0 0 2px ${m.color}` } : {}} data-testid={`measure-${m.k}`}>
              <div className="label-mini">{m.label}</div>
              <div className="font-mono text-lg mt-1" style={{ color: m.color }}>{latest?.[m.k] ?? '—'}</div>
              {change !== null && <div className={`text-[10px] mt-0.5 font-mono ${change < 0 ? 'text-[var(--c-green-ink)]' : 'text-muted-foreground'}`}>{change > 0 ? '+' : ''}{change} cm</div>}
            </button>
          );
        })}
      </div>
    </>
  );
}

function PhotosTab({ photos, reload }) {
  const [open, setOpen] = useState(false);
  const [angle, setAngle] = useState('front');
  const [note, setNote] = useState('');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const [cmpAngle, setCmpAngle] = useState('front');
  const [slider, setSlider] = useState(50);

  const onPick = (e) => { const f = e.target.files[0]; if (f) { setFile(f); setPreview(URL.createObjectURL(f)); } };
  const save = async () => {
    if (!file) return toast.error('Choose a photo');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file); fd.append('angle', angle); fd.append('note', note);
      await api.post('/progress-photos/upload', fd);
      toast.success('Photo uploaded'); setOpen(false); setFile(null); setPreview(''); setNote(''); reload();
    } catch { toast.error('Upload failed'); } finally { setUploading(false); }
  };
  const del = async (id) => { if (!confirm('Delete this photo?')) return; await api.delete(`/progress-photos/${id}`); reload(); };

  const ofAngle = photos.filter(p => (p.angle || 'front') === cmpAngle).sort((a, b) => a.date.localeCompare(b.date));
  const before = ofAngle[0], current = ofAngle[ofAngle.length - 1];

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-photo-btn" className="bg-[#FF6FA8] text-[#081726] font-semibold"><Camera className="w-4 h-4 mr-1.5" /> Add Photo</Button></DialogTrigger>
          <DialogContent className="bg-regen-card border-[var(--c-hair)]">
            <DialogHeader><DialogTitle>Add Progress Photo</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2">
                {['front', 'side', 'back'].map(a => (
                  <button key={a} onClick={() => setAngle(a)} data-testid={`angle-${a}`} className={`py-2 rounded-xl text-sm font-semibold capitalize transition ${angle === a ? 'bg-[#FF6FA8] text-[#081726]' : 'bg-[var(--c-film)] text-muted-foreground'}`}>{a}</button>
                ))}
              </div>
              <input ref={fileRef} type="file" accept="image/*" onChange={onPick} data-testid="photo-file" className="hidden" />
              <button onClick={() => fileRef.current?.click()} className="w-full border border-dashed border-[var(--c-hair)] rounded-xl py-8 flex flex-col items-center gap-2 text-muted-foreground">
                <Upload className="w-6 h-6" /> <span className="text-sm">Tap to choose a photo</span>
              </button>
              {preview && <img src={preview} alt="preview" className="w-full max-h-56 object-cover rounded-lg" />}
              <Input value={note} onChange={e => setNote(e.target.value)} data-testid="photo-note" placeholder="Note (e.g. Week 4)" className="bg-transparent border-[var(--c-hair)]" />
              <Button onClick={save} disabled={uploading} data-testid="photo-save" className="w-full bg-[#FF6FA8] text-[#081726] font-semibold">{uploading ? 'Uploading…' : 'Save Photo'}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Before vs Current comparison */}
      {before && current && before.id !== current.id && (
        <div className="regen-card-elevated p-4" data-testid="photo-compare">
          <div className="flex items-center justify-between mb-3">
            <div className="label-mini">Before vs Current</div>
            <div className="flex gap-1">
              {['front', 'side', 'back'].map(a => (
                <button key={a} onClick={() => setCmpAngle(a)} className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${cmpAngle === a ? 'bg-[#FF6FA8] text-[#081726]' : 'bg-[var(--c-film)] text-muted-foreground'}`}>{a}</button>
              ))}
            </div>
          </div>
          <div className="relative aspect-[3/4] rounded-xl overflow-hidden">
            <img src={imgSrc(before.photo_url)} alt="before" className="absolute inset-0 w-full h-full object-cover" />
            <img src={imgSrc(current.photo_url)} alt="current" className="absolute inset-0 w-full h-full object-cover" style={{ opacity: slider / 100 }} />
            <span className="absolute top-2 left-2 pill bg-black/60 text-[var(--c-ink)] text-[10px]">{before.date}</span>
            <span className="absolute top-2 right-2 pill bg-black/60 text-[var(--c-ink)] text-[10px]">{current.date}</span>
          </div>
          <input type="range" min="0" max="100" value={slider} onChange={e => setSlider(+e.target.value)} data-testid="compare-slider" className="w-full mt-3 accent-[#FF6FA8]" />
          <div className="flex justify-between text-[10px] text-muted-foreground"><span>Before</span><span>Current</span></div>
        </div>
      )}

      {photos.length === 0 ? (
        <div className="regen-card-elevated p-8 text-center"><Camera className="w-8 h-8 mx-auto mb-3 text-[#FF6FA8]" /><div className="text-sm text-muted-foreground">Track your transformation visually. Add photos weekly.</div></div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {[...photos].reverse().map(p => (
            <div key={p.id} className="regen-card-elevated overflow-hidden relative" data-testid={`photo-${p.id}`}>
              <img src={imgSrc(p.photo_url)} alt={p.note} className="w-full aspect-[3/4] object-cover" />
              <span className="absolute top-2 left-2 pill bg-black/60 text-[var(--c-ink)] text-[10px] capitalize">{p.angle || 'front'}</span>
              <div className="p-2.5"><div className="text-[10px] text-muted-foreground font-mono">{p.date}</div>{p.note && <div className="text-xs mt-0.5 truncate">{p.note}</div>}</div>
              <button onClick={() => del(p.id)} className="absolute bottom-2 right-2 w-7 h-7 rounded-full bg-black/60 flex items-center justify-center"><span className="text-red-400 text-xs">✕</span></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ScoreTab({ history }) {
  if (!history) return null;
  const cards = [
    { label: 'Current', value: history.current, color: '#65A30D' },
    { label: '7-Day Avg', value: history.avg_7d, color: '#00C8FF' },
    { label: '30-Day Avg', value: history.avg_30d, color: '#A78BFA' },
    { label: 'Best', value: history.best, color: '#FFB800', icon: Award },
    { label: 'Streak', value: history.streak, color: '#FF6FA8', icon: Flame },
  ];
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
        {cards.map(c => (
          <div key={c.label} className="regen-card-elevated p-3 text-center" data-testid={`score-${c.label}`}>
            {c.icon && <c.icon className="w-4 h-4 mx-auto mb-1" style={{ color: c.color }} />}
            <div className="font-mono text-xl" style={{ color: c.color }}>{c.value}</div>
            <div className="text-[10px] text-muted-foreground mt-0.5">{c.label}</div>
          </div>
        ))}
      </div>
      <div className="regen-card-elevated p-5">
        <div className="label-mini">Score History</div>
        <div className="h-48 mt-3">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={history.series} margin={{ left: -18, right: 6, top: 6 }}>
              <XAxis dataKey="date" tickFormatter={d => d.slice(5)} tick={{ fontSize: 9, fill: '#7E8C9E' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
              <YAxis domain={[40, 100]} tick={{ fontSize: 9, fill: '#7E8C9E' }} axisLine={false} tickLine={false} width={28} />
              <Tooltip contentStyle={{ background: '#101E30', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12 }} />
              <Line type="monotone" dataKey="score" stroke='#65A30D' strokeWidth={2.5} dot={false} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function KCard({ label, value, unit, color }) {
  return (
    <div className="regen-card-elevated p-3 text-center">
      <div className="label-mini">{label}</div>
      <div className="font-mono text-lg mt-1" style={color ? { color } : {}}>{value}</div>
      <div className="text-[9px] text-muted-foreground">{unit}</div>
    </div>
  );
}
